import { CoordinatesSection } from '@/components/attractions/CoordinatesSection';
import type { Attraction } from '@/config/objects/attractions';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EmptyTableState,
  Loading,
} from '@startupquickstart/frontend';
import { CheckCircle2, MapPinOff, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

interface HealthLocationListProps {
  attractions: Attraction[] | undefined;
  isLoading?: boolean;
  /**
   * Called after a verify-and-save round-trips so the parent can refetch
   * the attractions query and the now-verified row drops out of the list.
   */
  onAttractionUpdated?: () => void;
}

interface UnverifiedRowProps {
  attraction: Attraction;
  onOpen: () => void;
}

const formatAddress = (a: Attraction): string => {
  const parts: string[] = [];
  if (a.streetAddress) parts.push(a.streetAddress);
  const cityState = [a.city, a.state].filter(Boolean).join(', ');
  if (cityState) parts.push(cityState);
  if (a.postalCode) parts.push(a.postalCode);
  return parts.join(' • ') || 'No address';
};

const hasCoords = (a: Attraction): boolean =>
  a.latitude != null && a.longitude != null;

function UnverifiedRow({ attraction, onOpen }: UnverifiedRowProps) {
  const reasonLabel = !hasCoords(attraction)
    ? 'Missing coordinates'
    : 'Coordinates unverified';
  const Icon = !hasCoords(attraction) ? MapPinOff : ShieldAlert;
  const accentClass = !hasCoords(attraction)
    ? 'text-amber-700 bg-amber-50 border-amber-200'
    : 'text-slate-700 bg-slate-50 border-slate-200';

  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border bg-card p-4 shadow-sm">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/attractions/${attraction.id}/overview`}
            className="truncate text-sm font-semibold text-primary hover:underline"
          >
            {attraction.name}
          </Link>
          <Badge variant="outline" className={`gap-1 ${accentClass}`}>
            <Icon className="h-3 w-3" />
            {reasonLabel}
          </Badge>
        </div>
        <p className="truncate text-xs text-muted-foreground">{formatAddress(attraction)}</p>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button variant="default" size="sm" onClick={onOpen}>
          <ShieldCheck className="h-3.5 w-3.5" />
          Verify
        </Button>
      </div>
    </div>
  );
}

/**
 * Renders the "Location" health view: every attraction in the supplied list
 * whose coordinates are missing or not yet externally verified.
 *
 * Each row opens a dialog hosting the existing CoordinatesSection, so the
 * verify + map + save flow is identical to the attraction detail page —
 * staff can fix all of them in one sitting without bouncing between pages.
 */
export function HealthLocationList({
  attractions,
  isLoading,
  onAttractionUpdated,
}: HealthLocationListProps) {
  // Track which row's dialog is open by ID, then look up the live record
  // from the upstream list on each render. This way once a save round-trips
  // and the parent invalidates the list, the dialog re-renders with the
  // fresh `isAddressVerified` flag instead of holding onto a stale
  // pre-save snapshot of the row.
  const [openedAttractionId, setOpenedAttractionId] = useState<string | null>(null);
  const openedAttraction = useMemo(
    () =>
      openedAttractionId
        ? (attractions ?? []).find((a) => a.id === openedAttractionId) ?? null
        : null,
    [openedAttractionId, attractions],
  );

  const { unverified, total } = useMemo(() => {
    const all = attractions ?? [];
    const filtered = all.filter((a) => !a.isAddressVerified);
    return { unverified: filtered, total: all.length };
  }, [attractions]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (total === 0) {
    return (
      <EmptyTableState
        icon={<MapPinOff className="h-6 w-6" />}
        title="No attractions to check"
        description="Add attractions to this scope and they will be checked here."
      />
    );
  }

  if (unverified.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
        <CheckCircle2 className="h-5 w-5" />
        <div>
          <p className="text-sm font-semibold">All locations verified</p>
          <p className="text-xs">
            Every attraction in scope ({total}) has externally-verified
            coordinates.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">
        Showing {unverified.length} of {total} attraction
        {total === 1 ? '' : 's'} that {unverified.length === 1 ? "doesn't" : "don't"}{' '}
        have a verified location.
      </div>

      <div className="space-y-2">
        {unverified.map((a) => (
          <UnverifiedRow
            key={a.id}
            attraction={a}
            onOpen={() => setOpenedAttractionId(a.id)}
          />
        ))}
      </div>

      <Dialog
        open={!!openedAttraction}
        onOpenChange={(next) => {
          if (!next) {
            setOpenedAttractionId(null);
            onAttractionUpdated?.();
          }
        }}
      >
        {/*
          Fill ~98% of the viewport so the embedded CoordinatesSection
          (warning banner + interactive map + lat/lng readout + verify
          actions) can lay out comfortably without the user needing to
          tap the inner Expand button. Cap at 1800px for ultra-wide
          monitors so the dialog doesn't sprawl across the full screen.
          The `sm:max-w-[98vw]` override is needed because shadcn's
          DialogContent ships an `sm:max-w-lg` cap that otherwise wins.
        */}
        <DialogContent className="flex h-[95vh] w-[98vw] max-w-[1800px] flex-col p-0 sm:max-w-[98vw]">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle className="truncate">
              {openedAttraction?.name ?? 'Verify location'}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 p-3">
            {openedAttraction && (
              <CoordinatesSection
                attraction={openedAttraction}
                layout="wide"
                onClose={() => setOpenedAttractionId(null)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
